import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {IAiChatMessage} from "../interface/ai-chat.interface";

/**
 * State management service for the global AI Chat panel.
 * Manages panel visibility, current module context, conversation state.
 */
@Injectable({
    providedIn: 'root'
})
export class AiChatStateService {

    private panelOpenSubject = new BehaviorSubject<boolean>(false);
    private currentModuleSubject = new BehaviorSubject<string | null>(null);
    private currentRouteSubject = new BehaviorSubject<string | null>(null);
    private messagesSubject = new BehaviorSubject<IAiChatMessage[]>([]);
    private currentConversationIdSubject = new BehaviorSubject<number | null>(null);
    private loadingSubject = new BehaviorSubject<boolean>(false);

    panelOpen$: Observable<boolean> = this.panelOpenSubject.asObservable();
    currentModule$: Observable<string | null> = this.currentModuleSubject.asObservable();
    currentRoute$: Observable<string | null> = this.currentRouteSubject.asObservable();
    messages$: Observable<IAiChatMessage[]> = this.messagesSubject.asObservable();
    currentConversationId$: Observable<number | null> = this.currentConversationIdSubject.asObservable();
    loading$: Observable<boolean> = this.loadingSubject.asObservable();

    get isPanelOpen(): boolean {
        return this.panelOpenSubject.getValue();
    }

    get currentModule(): string | null {
        return this.currentModuleSubject.getValue();
    }

    get currentConversationId(): number | null {
        return this.currentConversationIdSubject.getValue();
    }

    togglePanel(): void {
        this.panelOpenSubject.next(!this.panelOpenSubject.getValue());
    }

    openPanel(): void {
        this.panelOpenSubject.next(true);
    }

    closePanel(): void {
        this.panelOpenSubject.next(false);
    }

    setCurrentModule(moduleAlias: string): void {
        this.currentModuleSubject.next(moduleAlias);
    }

    setCurrentRoute(route: string): void {
        this.currentRouteSubject.next(route);
    }

    setConversationId(id: number | null): void {
        this.currentConversationIdSubject.next(id);
    }

    addMessage(message: IAiChatMessage): void {
        const current = this.messagesSubject.getValue();
        this.messagesSubject.next([...current, message]);
    }

    setMessages(messages: IAiChatMessage[]): void {
        this.messagesSubject.next(messages);
    }

    clearMessages(): void {
        this.messagesSubject.next([]);
        this.currentConversationIdSubject.next(null);
    }

    setLoading(loading: boolean): void {
        this.loadingSubject.next(loading);
    }

    startNewConversation(): void {
        this.clearMessages();
    }
}
